/**
 * VerifyEmail — handles the email verification flow.
 * When user clicks the verification link they land here.
 */
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
      return;
    }
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus("success");
      setMessage(res.data.message || "Email verified successfully!");
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Verification failed. The link may be invalid or expired.");
    }
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4">
      <Card className="bg-app-card border-white/10 p-8 max-w-md w-full text-center">
        {status === "loading" && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 animate-spin text-violet-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-app mb-2">Verifying your email...</h2>
            <p className="text-sm text-app-secondary">Please wait while we confirm your email address.</p>
          </div>
        )}

        {status === "success" && (
          <div className="py-8">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-app mb-2">Email Verified!</h2>
            <p className="text-sm text-app-secondary mb-6">{message}</p>
            <Link to="/">
              <Button className="bg-violet-600 hover:bg-violet-500 text-white">Go to Login</Button>
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="py-8">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-app mb-2">Verification Failed</h2>
            <p className="text-sm text-app-secondary mb-6">{message}</p>
            <div className="flex flex-col gap-3 items-center">
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-app">Back to Login</Button>
              </Link>
              <p className="text-xs text-app-secondary flex items-center gap-1">
                <Mail className="w-3 h-3" /> Contact support if you need help
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
